<template>
  <div class="container-fluid">
    <div class="loading" v-if="loading">Loading&hellip;</div>

    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Cuenta corriente — {{ client.name || '...' }}</h1>
          <p v-if="client.name" class="view-header__subtitle">
            <span v-if="client.business">{{ client.business }}</span>
            <span v-if="client.address"> · {{ client.address }}</span>
            <span v-if="client.phone_1"> · Tel. {{ client.phone_1 }}</span>
          </p>
        </div>
        <div class="view-header__actions">
          <button type="button" class="btn view-header__btn" @click="ccClientesLink">
            <i class="bi bi-arrow-left"></i>
            Volver
          </button>
          <button type="button" class="btn view-header__btn" @click="exportPDF()">
            <i class="bi bi-download"></i>
            Exportar PDF
          </button>
          <button type="button" class="btn view-header__btn view-header__btn--primary" @click="showPaseModal = true">
            <i class="bi bi-plus-lg"></i>
            Nuevo pase a cuenta
          </button>
        </div>
      </div>
    </div>

    <!-- BALANCE -->
    <div v-if="balanceFull" class="stats-card">
      <div class="stats-card__value">{{ formatoDinamicoMoneda(balanceTotal) }}</div>
      <div class="stats-card__label">Saldo actual de cuenta corriente</div>
      <button v-if="hasMultipleBranches" type="button" class="form-card__btn mt-3" @click="balanceFull = false">
        <i class="bi bi-bar-chart"></i>
        Ver por punto de venta
      </button>
    </div>
    <div v-else>
      <div class="row">
        <div v-for="(balance, index) in balanceList" :key="index" class="col-md-4 col-sm-6">
          <div class="stats-card">
            <div class="stats-card__value">{{ formatoDinamicoMoneda(balance) }}</div>
            <div class="stats-card__label">Punto de venta {{ index }}</div>
          </div>
        </div>
      </div>
      <div class="text-center mb-3">
        <button type="button" class="form-card__btn" @click="balanceFull = true">
          <i class="bi bi-arrow-left"></i>
          Ver total
        </button>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <q-select v-model.number="filters.type" :options="typeFilterOptions" />
          </div>
          <div class="filters-panel__field">
            <Daterange :opendir="'left'" :filtered="true" @datepicked="dateUpdated"/>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLA -->
    <div class="data-table">
      <div class="table-responsive">
        <table class="table mb-0">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Vencimiento</th>
              <th>Comprobante</th>
              <th>Concepto</th>
              <th class="text-right">Debe</th>
              <th class="text-right">Haber</th>
              <th class="text-right">Saldo</th>
              <th class="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="cc in accounts" :key="cc.id">
              <td>{{ cc.created_at }}</td>
              <td>{{ cc.duedate !== null ? formatoDate2Fecha(cc.duedate) : '-' }}</td>
              <td>
                <a v-if="cc.sale_id !== null" :href="'/ventas/index/detalle/' + cc.sale_id">{{ cc.number }}</a>
                <a v-else-if="cc.receipt_id !== null" :href="'/ventas/cobranzas/detalle-recibo/' + cc.id">{{ cc.number }}</a>
                <span v-else class="data-table__muted">Pase manual</span>
              </td>
              <td class="data-table__muted">{{ cc.notes }}</td>
              <td class="text-right">{{ formatoDinamicoMoneda(cc.in) }}</td>
              <td class="text-right">{{ formatoDinamicoMoneda(cc.out) }}</td>
              <td class="text-right">
                <span class="data-table__primary" :class="cc.saldo >= 0 ? 'text-success' : 'text-danger'">
                  {{ formatoDinamicoMoneda(cc.saldo) }}
                </span>
              </td>
              <td class="text-right">
                <div class="table-action-group">
                  <button v-if="cc.sale_id !== null" type="button" class="table-action-btn" title="Anular comprobante" @click="cancelSale(cc.sale_id)">
                    <i class="bi bi-trash"></i>
                  </button>
                  <button v-if="cc.receipt_id !== null" type="button" class="table-action-btn" title="Descargar PDF" @click="getReceiptPDF(cc)">
                    <i class="bi bi-filetype-pdf"></i>
                  </button>
                  <button v-if="cc.receipt_id !== null" type="button" class="table-action-btn" title="Cancelar pago" @click="cancelPayment(cc.id)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="accounts.length === 0">
              <td colspan="8" class="data-table__empty">Sin movimientos</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- PAGINACION -->
      <div class="data-table__footer">
        <div class="data-table__info">
          Página {{ filters.page }} de {{ totalPages }}
        </div>
        <ul class="data-table__pagination">
          <li class="page-item" :class="{ 'disabled': filters.page === 1 }">
            <a class="page-link" href="#" @click.prevent="prevPage()">
              <i class="bi bi-chevron-left"></i>
            </a>
          </li>
          <li v-for="index in totalPages" :key="index" class="page-item" :class="{ 'active': filters.page === index }">
            <a class="page-link" href="#" @click.prevent="changePage(index)">{{ index }}</a>
          </li>
          <li class="page-item" :class="{ 'disabled': filters.page >= totalPages }">
            <a class="page-link" href="#" @click.prevent="nextPage()">
              <i class="bi bi-chevron-right"></i>
            </a>
          </li>
        </ul>
        <div class="data-table__page-size">
          <q-select v-model.number="filters.itemsPerPage" :options="itemsPerPageOptions" />
        </div>
      </div>
    </div>

    <!-- MODAL NUEVO PASE A CUENTA -->
    <NuevoPaseCuentaCliente
      v-if="showPaseModal"
      :clientId="clientId"
      @confirmsave="onPaseSaved"
      @close="showPaseModal = false"
    />
  </div>
</template>

<script>
import Daterange from '../../global/Daterange-component.vue';
import NuevoPaseCuentaCliente from './Nuevo-pase-cuenta-cliente.vue';
import formatDateMixin from "../../../mixins/formatDateMixin";
import formatMoneyMixin from "../../../mixins/formatMoneyMixin";
import myaxios from 'axios';
import QSelect from '@/components/shared/QSelect.vue';

export default {
  props: ['clientId'],
  components: {
    Daterange,
    NuevoPaseCuentaCliente,
    QSelect,
  },
  mixins: [formatDateMixin, formatMoneyMixin],
  data: function () {
    return {
      loading: false,
      showPaseModal: false,
      client: {},
      accounts: [],
      balanceFull: true,
      balanceList: [],
      balanceTotal: 0,
      totalPages: 1,
      filters: {
        type: 0,
        startDate: null,
        endDate: null,
        page: 1,
        itemsPerPage: 15,
      },
    };
  },
  computed: {
    typeFilterOptions() {
      return [
        { value: 0, label: 'Todos los movimientos' },
        { value: 1, label: 'Movimientos a saldar' },
        { value: 2, label: 'Movimientos saldados' },
      ];
    },
    itemsPerPageOptions() {
      return [
        { value: 15, label: '15 por página' },
        { value: 25, label: '25 por página' },
        { value: 50, label: '50 por página' },
      ];
    },
    hasMultipleBranches() {
      return this.balanceList && Object.keys(this.balanceList).length > 1;
    },
  },
  mounted() {
    this.setDefaultDateRange();
    this.getClient(this.clientId);
    this.getBalance();
    this.getCC();
  },
  methods: {
    refresh() {
      this.getBalance();
      this.getCC();
    },
    onPaseSaved() {
      this.showPaseModal = false;
      this.refresh();
    },
    getClient(clientId) {
      this.$http.get('/api/clients/getClient/' + clientId)
        .then(response => { this.client = response.data.data; })
        .catch(error => alert(error));
    },
    getBalance() {
      this.$http.get('/api/clients/getBalances/' + this.clientId)
        .then(response => {
          this.balanceTotal = response.data.data.total;
          this.balanceList = response.data.data.list;
        })
        .catch(error => alert(error));
    },
    getCC() {
      this.$http.post('/api/clients/getAccount/' + this.clientId, this.filters)
        .then(response => {
          if (response.status === 200) {
            this.accounts = response.data.data.accounts;
            this.totalPages = response.data.data.totalPages;
          } else {
            this.accounts = [];
            this.totalPages = 0;
          }
        })
        .catch(error => alert(error));
    },
    prevPage() {
      if (this.filters.page > 1) this.filters.page -= 1;
    },
    nextPage() {
      if (this.filters.page < this.totalPages) this.filters.page += 1;
    },
    changePage(page) {
      this.filters.page = page;
    },
    cancelSale(id) {
      if (!confirm("¿Está seguro que desea anular este comprobante? Los comprobantes anulados pueden verse en la vista de ventas")) return;
      this.$http.post('/api/sales/cancel', { id })
        .then(response => {
          if (response.data.success) {
            this.refresh();
          } else {
            alert(response.data.message || 'No se pudo anular el comprobante.');
          }
        })
        .catch(error => alert(error));
    },
    cancelPayment(id) {
      if (!confirm("¿Está seguro que desea eliminar este pago? Se desarmarán también todas las imputaciones en las que se encuentre asociado")) return;
      this.$http.get('/api/collections/cancelPayment/' + id)
        .then(response => {
          if (response.data.success) {
            this.refresh();
          } else {
            alert(response.data.message || 'No se pudo cancelar el pago.');
          }
        })
        .catch(error => alert(error));
    },
    dateUpdated(date) {
      this.filters.startDate = date.startDate;
      this.filters.endDate = date.endDate;
    },
    getReceiptPDF(data) {
      const filename = data.number + ' ' + Date.now() + '.pdf';
      this.$http.get("/api/collections/checkReceiptPDF/" + data.id).then((res) => {
        const uninterceptedAxiosInstance = myaxios.create();
        uninterceptedAxiosInstance.get(res.data.data.urlInvoice, { responseType: 'blob' }).then((response) => {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          URL.revokeObjectURL(link.href);
        });
      });
    },
    exportPDF() {
      this.$http.post("/api/reports/PDFdetailccc/" + this.clientId, this.filters);
    },
    ccClientesLink() {
      this.$router.push({ name: 'Cuenta corriente de clientes' });
    },
    setDefaultDateRange() {
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - 30);
      this.filters.startDate = this.formatDate(startDate);
      this.filters.endDate = this.formatDate(today);
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const day = ('0' + date.getDate()).slice(-2);
      return `${day}/${month}/${year}`;
    },
  },
  watch: {
    'filters.type'() { this.getCC(); },
    'filters.itemsPerPage'() { this.getCC(); },
    'filters.page'() { this.getCC(); },
    'filters.startDate'() { this.getCC(); },
    'filters.endDate'() { this.getCC(); },
  },
};
</script>
